import { useMutation, useQuery, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import {
  configureIntegration,
  getAgentTemplateMoreData,
  getAllAvailableIntegrations,
  getAvailableIntegration,
  getAvailableIntegrations,
  getCatalogAvailableTools,
  getAvailableTools,
  getHelpCenterArticle,
  purchaseCatalogIntegration
} from './api';
import { AvailableIntegrationResponse, HelpCenterArticleResponse, IntegrationToolResponse } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';

export function useGetAvailableIntegrations(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }>({
    queryKey: ['integrations', params],
    queryFn: () => getAvailableIntegrations(params),
    ...options
  });
}

export function useGetAllAvailableIntegrations(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }>({
    queryKey: ['all_integrations', params],
    queryFn: () => getAllAvailableIntegrations(params),
    ...options
  });
}

export function useGetAvailableIntegration(options?: UseQueryOptions<AvailableIntegrationResponse, Error>) {
  const { integrationId } = useParams<{ integrationId: string }>();

  return useQuery<AvailableIntegrationResponse>({
    queryKey: ['integration', integrationId],
    queryFn: () => getAvailableIntegration(integrationId!),
    enabled: Boolean(integrationId),
    ...options
  });
}

export function useConfigureIntegration() {
  const { integrationId } = useParams<{ integrationId: string }>();

  return useMutation({
    mutationFn: (payload: any) => configureIntegration(integrationId ?? payload.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['integration'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetAvailableTools(id: string, options?: UseQueryOptions<any[], Error>) {
  return useQuery<any[]>({
    queryKey: ['integration_tools', id],
    queryFn: () => getAvailableTools(id),
    enabled: Boolean(id),
    ...options
  });
}

export function useGetCatalogAvailableTools(
  code: string | null | undefined,
  options?: UseQueryOptions<IntegrationToolResponse[], Error>
) {
  return useQuery<IntegrationToolResponse[]>({
    queryKey: ['catalog_integration_tools', code],
    queryFn: () => getCatalogAvailableTools(code!),
    enabled: Boolean(code),
    ...options
  });
}

export function usePurchaseCatalogIntegration(options?: UseMutationOptions<any, Error, string>) {
  return useMutation({
    ...options,
    mutationFn: (code: string) => purchaseCatalogIntegration(code),
    onSuccess: async (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['all_integrations'],
        refetchType: 'active'
      });
      await options?.onSuccess?.(...args);
    }
  });
}

export function useGetAgencyAgentTemplateMoreData(
  templateId?: number | string,
  options?: UseQueryOptions<{ data: string[] }, Error>
) {
  return useQuery<{ data: string[] }>({
    queryKey: ['agencyAgentTemplateMoreData', templateId],
    queryFn: () => getAgentTemplateMoreData(templateId!),
    enabled: Boolean(templateId),
    ...options
  });
}

export function useGetHelpCenterArticle(
  articleId: string | number | null | undefined,
  options?: Omit<UseQueryOptions<HelpCenterArticleResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<HelpCenterArticleResponse>({
    queryKey: ['help_center_article', articleId],
    queryFn: () => getHelpCenterArticle(articleId!),
    enabled: Boolean(articleId),
    ...options
  });
}
